/**
 * context-zone.js — the morph.
 *
 * The only part of the bar that changes with the selection. Everything around
 * it — both anchors in row 2, all of row 1 — is fixed, which is what makes a
 * control targetable without looking.
 *
 * ── Two models, two different responses ───────────────────────────────────
 * `SelectionScope` emits `{identity, viewModel, identityChanged}`.
 *
 *   identityChanged  → REBUILD (a different pane, a real morph, announced)
 *   otherwise        → REFRESH (same pane, new values, silent)
 *
 * That split is the whole reason the two models exist. A peer lock arriving, an
 * undo, a responsive edit — none of those should re-mount a pane and re-announce
 * a scope the user never left. The CMD-2 tests already pin
 * `identityChanged === false` for a lock; this is the consumer that makes it
 * matter.
 *
 * ── The height never changes ──────────────────────────────────────────────
 * The zone is `height: var(--pb-cb-row2-h)` and has been since CMD-2, when it
 * was empty. A row that grew when its first pane arrived would move the canvas
 * under the cursor — the mis-click generator this whole design exists to avoid.
 * The browser gate asserts the height is identical across every scope AND that
 * the scope actually changed while it stayed identical; without that second
 * half the first passes vacuously.
 *
 * ── One announcement per morph ────────────────────────────────────────────
 * Contracts §3: a single `aria-live="polite"` region names the new scope once.
 * Individual controls do not announce. Twelve buttons appearing is one event to
 * a sighted user and would be twelve to a screen-reader user.
 */

import { PANE_BUILDERS, buildLabelOnlyPane } from './context-panes.js';
import { releaseOperationControls } from './command-operation-state.js';

/** The accent each scope paints its pane swatch with. */
const SCOPE_ACCENT = Object.freeze({
  page: 'page',
  section: 'section',
  multi: 'section',
  text: 'element',
  image: 'element',
  element: 'element'
});

export class ContextZone {
  /**
   * @param {HTMLElement} container — the `row2.context` slot
   * @param {import('./selection-scope.js').SelectionScope} scope
   */
  constructor(container, scope) {
    this._container = container;
    this._scope = scope;
    this._off = null;
    this._pane = null;
    this._kind = null;
    this._render();
    this._listen();
  }

  destroy() {
    if (typeof this._off === 'function') this._off();
    this._off = null;
    releaseOperationControls(this._pane);
    this._pane = null;
  }

  /** For tests and the geometry gate. */
  get kind() {
    return this._kind;
  }

  _render() {
    this._pane = document.createElement('div');
    this._pane.className = 'pb-cb-pane';
    this._container.appendChild(this._pane);

    // ONE region for the whole bar's scope changes. The kit has no live-region
    // primitive — `collection-chrome.js`, `contrast-badge.js` and four modals
    // each build their own the same way — so this follows the house pattern
    // (`.pb-sr-only` + `aria-live="polite"`) rather than inventing a kit API
    // that does not exist.
    this._live = document.createElement('div');
    this._live.className = 'pb-sr-only';
    this._live.setAttribute('aria-live', 'polite');
    // NOT aria-atomic: the region holds one sentence and is replaced wholesale.
    this._container.appendChild(this._live);
  }

  _listen() {
    // `subscribe` emits immediately with the current scope (contract S2): a bar
    // that boots blank is a bug, and a pane that only appears after the first
    // selection change is the same bug with a longer fuse.
    this._off = this._scope.subscribe((change) => this._apply(change));
  }

  _apply({ identity, viewModel, identityChanged }) {
    if (!identity || !viewModel) return;
    if (identityChanged || this._kind === null) this._rebuild(identity, viewModel);
    else this._refresh(identity, viewModel);
  }

  _rebuild(identity, viewModel) {
    const kind = identity.kind;
    this._kind = kind;
    this._container.dataset.scope = kind;
    this._container.dataset.accent = SCOPE_ACCENT[kind] || 'page';

    const build = PANE_BUILDERS[kind] || buildLabelOnlyPane;
    releaseOperationControls(this._pane);
    this._pane.replaceChildren(build(viewModel));

    // Out and in as ONE event, so the reader never sees a half-transitioned
    // bar. Re-triggering the animation needs the class removed, a reflow read,
    // and the class re-added — assigning it twice in a frame does nothing.
    this._pane.classList.remove('pb-cb-pane-enter');
    void this._pane.offsetWidth;
    this._pane.classList.add('pb-cb-pane-enter');

    this._announce(viewModel, kind);
  }

  /**
   * Same pane, new values. Rebuilding the DOM here would work and would be
   * wrong: it drops focus, restarts the morph animation, and re-announces a
   * scope nobody left. A lock arriving mid-edit must not do any of that.
   */
  _refresh(identity, viewModel) {
    const build = PANE_BUILDERS[identity.kind] || buildLabelOnlyPane;
    const active = document.activeElement;
    const activeLabel =
      active && this._pane.contains(active) ? active.getAttribute('aria-label') : null;

    releaseOperationControls(this._pane);
    this._pane.replaceChildren(build(viewModel));

    // Focus survives a value refresh by NAME, not by index: the button list can
    // change length when a capability appears or disappears, and an index would
    // silently move focus to a different verb — the worst outcome for a
    // keyboard user, who cannot see that it moved.
    if (activeLabel) {
      for (const btn of this._pane.querySelectorAll('button')) {
        if (btn.getAttribute('aria-label') === activeLabel && !btn.disabled) {
          btn.focus();
          break;
        }
      }
    }
  }

  _announce(viewModel, kind) {
    const what = viewModel.label || kind;
    const message =
      kind === 'page'
        ? `Page: ${what}`
        : kind === 'multi'
          ? what
          : `${kind === 'section' ? 'Section' : 'Element'} selected: ${what}`;
    // Same text twice in a row is not re-announced by most screen readers, and
    // re-selecting the same section legitimately produces the same sentence.
    // Clearing first makes the second one land.
    if (this._live.textContent === message) this._live.textContent = '';
    this._live.textContent = message;
  }
}
